import { Button } from 'antd';
import dayjs from 'dayjs';
import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { ICON_NAME } from '../../Icon';

export interface YearPickerValue {
    year: number;
}

interface IProps {
    startYear?: number;
    onChange?: (value: YearPickerValue) => void;
}

const INITIAL_YEAR_COUNT = 12;

export default function YearPicker({ startYear, onChange }: IProps) {
    const currentYear = dayjs().year();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const firstYear = Math.min(
        startYear ?? currentYear - INITIAL_YEAR_COUNT + 1,
        currentYear,
    );
    const yearsContainerRef = useRef<HTMLDivElement>(null);
    const dragStartXRef = useRef(0);
    const dragStartScrollLeftRef = useRef(0);
    const hasDraggedRef = useRef(false);

    const years = useMemo(() => {
        return Array.from(
            { length: currentYear - firstYear + 1 },
            (_, index) => firstYear + index,
        );
    }, [currentYear, firstYear]);

    useEffect(() => {
        const selectedYearButton = yearsContainerRef.current?.querySelector(
            `[data-year="${selectedYear}"]`,
        );

        selectedYearButton?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        });
    }, [selectedYear]);

    const updateSelectedYear = (year: number) => {
        setSelectedYear(year);
        onChange?.({ year });
    };

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const container = yearsContainerRef.current;
        if (!container) return;

        dragStartXRef.current = event.clientX;
        dragStartScrollLeftRef.current = container.scrollLeft;
        hasDraggedRef.current = false;
        container.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const container = yearsContainerRef.current;
        if (!container || !container.hasPointerCapture(event.pointerId)) return;

        const distance = event.clientX - dragStartXRef.current;
        if (Math.abs(distance) > 4) {
            hasDraggedRef.current = true;
        }

        container.scrollLeft = dragStartScrollLeftRef.current - distance;
    };

    const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
        const container = yearsContainerRef.current;
        if (container?.hasPointerCapture(event.pointerId)) {
            container.releasePointerCapture(event.pointerId);
        }
    };

    return (
        <div className="flex min-w-0 items-center">
            <Button
                onClick={() => updateSelectedYear(selectedYear - 1)}
                disabled={selectedYear === firstYear}
                aria-label="Previous year"
                className="bt-ghost mr-2 !h-[36px] !w-[36px] shrink-0 !rounded-full border border-[--colorMain]"
                data-icon="true"
            >
                <Icon icon={ICON_NAME.ARROW} size="small" className="-rotate-90 mt-[1px] mr-[1px]" />
            </Button>

            <div
                ref={yearsContainerRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onClickCapture={event => {
                    if (hasDraggedRef.current) {
                        event.stopPropagation();
                        hasDraggedRef.current = false;
                    }
                }}
                className="flex min-w-0 flex-1 touch-pan-y cursor-grab snap-x overflow-x-auto select-none [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
            >
                {years.map(year => {
                    const isActive = selectedYear === year;

                    return (
                        <button
                            key={year}
                            data-year={year}
                            type="button"
                            onClick={() => updateSelectedYear(year)}
                            className={`
                                rounded-full flex h-[36px] shrink-0 snap-nearest items-center px-4 justify-center
                                text-[13px] font-[600]
                                transition-all duration-300
                                ${isActive
                                    ? 'text-[var(--colorActive)]'
                                    : 'text-[--colorSub] hover:bg-[var(--colorHover)] hover:text-[var(--colorActive)]'
                                }
                            `}
                        >
                            {year}
                        </button>
                    );
                })}
            </div>

            <Button
                onClick={() => updateSelectedYear(selectedYear + 1)}
                disabled={selectedYear === currentYear}
                aria-label="Next year"
                className="bt-ghost ml-2 !h-[36px] !w-[36px] shrink-0 !rounded-full border border-[--colorMain]"
                data-icon="true"
            >
                <Icon icon={ICON_NAME.ARROW} size="small" className="rotate-90" />
            </Button>
        </div>
    );
}
